import type { SearchClient } from '@libs/algolia';
import { AlgoliaIndice, createAlgoliaClient, getAlgoliaPersistentProductFilters } from '@libs/algolia';
import { getAlgoliaProductById } from '@libs/api-proxy-client';
import { getLocale } from '@libs/core';

import type { ProductSearchItem, QuerySearchItem, RecentSearchItem } from '../search.types';
import {
    getRedirectResponse,
    mapProductItems,
    mapQueryItems,
    type RawProductSuggestion,
    type RawQuerySuggestion,
    type RawSearchResponse,
    type SearchRedirectResponse,
} from './search-response-mapper';

export type { SearchRedirectResponse } from './search-response-mapper';

const QUERY_SUGGESTIONS_LIMIT = 6;
const PRODUCT_SUGGESTIONS_LIMIT = 2;
const HIGHLIGHT_PRE_TAG = '__aa-highlight__';
const HIGHLIGHT_POST_TAG = '__/aa-highlight__';

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const isOptionalFiniteNumber = (value: unknown) => value === undefined || isFiniteNumber(value);

const isValidPriceRange = (value: unknown) =>
    value === undefined ||
    (Array.isArray(value) && (value.length === 1 || value.length === 2) && value.every(isFiniteNumber));

const hasValidPriceData = (hit: RawProductSuggestion) => {
    const priceData = hit.priceData;

    if (!priceData) return false;

    return (
        isFiniteNumber(priceData.current) &&
        isFiniteNumber(priceData.list) &&
        isOptionalFiniteNumber(priceData.sale) &&
        isOptionalFiniteNumber(priceData.optionsFromList) &&
        isValidPriceRange(priceData.listRange) &&
        isValidPriceRange(priceData.saleRange)
    );
};

const searchClient: SearchClient = createAlgoliaClient({ caller: 'web:searchbar:autocomplete-client' });

export type SearchDataBatch = {
    query: string;
    recentItems: readonly RecentSearchItem[];
    queryItems: readonly QuerySearchItem[];
    queryCollectionLabels: readonly string[];
    productItems: readonly ProductSearchItem[];
    redirectResponses: readonly SearchRedirectResponse[];
};

type ReadSearchDataOptions = {
    analyticsTags: string[];
    clickAnalytics: boolean;
    query: string;
    recentItems: readonly RecentSearchItem[];
};

export async function readSearchProductById(query: string): Promise<ProductSearchItem | null> {
    const product = await getAlgoliaProductById({
        caller: 'web:contentful:autocomplete-check-for-redirects-client',
        id: query,
        locale: getLocale(),
    });

    return product ? mapProductItems([product])[0] : null;
}

export async function readSearchData({
    analyticsTags,
    clickAnalytics,
    query,
    recentItems,
}: ReadSearchDataOptions): Promise<SearchDataBatch> {
    const persistentFilters = getAlgoliaPersistentProductFilters();
    const requests = [
        {
            indexName: AlgoliaIndice.PRODUCTS_QUERY_SUGGESTIONS,
            query,
            params: {
                hitsPerPage: QUERY_SUGGESTIONS_LIMIT,
                highlightPreTag: HIGHLIGHT_PRE_TAG,
                highlightPostTag: HIGHLIGHT_POST_TAG,
            },
        },
        ...(query
            ? [
                  {
                      indexName: AlgoliaIndice.PRODUCTS,
                      query,
                      params: {
                          hitsPerPage: PRODUCT_SUGGESTIONS_LIMIT,
                          filters: persistentFilters,
                          facetingAfterDistinct: true,
                          clickAnalytics,
                          analyticsTags,
                          highlightPreTag: HIGHLIGHT_PRE_TAG,
                          highlightPostTag: HIGHLIGHT_POST_TAG,
                      },
                  },
              ]
            : []),
    ];

    const response = await searchClient.search<RawQuerySuggestion | RawProductSuggestion>(requests);
    const queryResponse = response.results[0] as RawSearchResponse<RawQuerySuggestion>;
    const productResponse = query ? (response.results[1] as RawSearchResponse<RawProductSuggestion>) : undefined;
    const queryItems = mapQueryItems(queryResponse.hits, query);

    return {
        query,
        recentItems,
        queryItems: queryItems.items,
        queryCollectionLabels: queryItems.queryCollectionLabels,
        productItems: productResponse
            ? mapProductItems(productResponse.hits.filter(hasValidPriceData), productResponse.queryID)
            : [],
        redirectResponses: [
            getRedirectResponse('querySuggestionsPlugin', queryResponse),
            ...(productResponse ? [getRedirectResponse('productSuggestionsPlugin', productResponse)] : []),
        ],
    };
}
